import { prepareInvoiceV2DataForSubmission } from "../../utils/invoiceV2Utils";
import { autosaveInvoiceV2 } from "../slices/invoiceV2Slice";

// Configurables
const AUTOSAVE_DEBOUNCE_MS = 3000; // 3 seconds debounce
const MIN_CHANGES_FOR_AUTOSAVE = 1; // Trigger autosave after any change to avoid data loss

// Track state between middleware calls
let autosaveTimer = null;
let changesSinceLastAutosave = 0;
let lastAutosaveTime = 0;
let autosaveInProgress = false;

/**
 * Middleware that tracks changes to the V2 invoice form and triggers autosave
 * when appropriate conditions are met
 */
const autosaveMiddlewareV2 = (store) => (next) => (action) => {
	// Process the action first to ensure state is updated
	const result = next(action);

	// Check if we should handle autosave for V2 actions only
	// V2 uses a table-based approach where users only set quantities on existing positions
	const shouldCheckForAutosave =
		action.type === "invoiceV2/setFormField" ||
		action.type === "invoiceV2/setInvoicePositionQuantity" ||
		action.type === "invoiceV2/setInvoicePositionComments" ||
		action.type === "invoiceV2/markFormDirty";

	if (shouldCheckForAutosave) {
		// Get current state after action was processed
		const state = store.getState();
		const invoiceV2State = state.invoiceV2;
		const isDirty = invoiceV2State?.changeTracking?.isDirty;
		const autosaving = invoiceV2State?.ui?.autosaving;
		const skipDirtyFlag = action.payload?.skipDirtyFlag === true;

		// Only validate when the user has edited the form (isDirty).
		// Do not force validation on bootstrap setFormField (e.g. projectId) - that
		// runs before fetchInvoice completes and would show false required errors.
		let updatedValidation = invoiceV2State?.validation || {};
		if (isDirty && !skipDirtyFlag) {
			store.dispatch({ type: "invoiceV2/validateForm" });
			updatedValidation =
				store.getState().invoiceV2?.validation || updatedValidation;
		}

		const canAutosave =
			isDirty &&
			!skipDirtyFlag &&
			updatedValidation.isValid &&
			!autosaving &&
			!autosaveInProgress;

		if (canAutosave) {
			changesSinceLastAutosave++;

			// Clear any existing timer
			if (autosaveTimer) {
				clearTimeout(autosaveTimer);
			}

			// Check if we should trigger autosave immediately or after debounce
			const timeSinceLastAutosave = Date.now() - lastAutosaveTime;
			const enoughChanges =
				changesSinceLastAutosave >= MIN_CHANGES_FOR_AUTOSAVE;
			const enoughTimePassed = timeSinceLastAutosave > 10000; // 10 seconds

			if (enoughChanges || enoughTimePassed) {
				// Set a debounced timer for autosave
				autosaveTimer = setTimeout(() => {
					const currentState = store.getState();
					const currentStateV2 = currentState.invoiceV2;

					// Only proceed if not already autosaving and form is dirty
					if (
						!currentStateV2.ui?.autosaving &&
						!autosaveInProgress &&
						currentStateV2.changeTracking?.isDirty
					) {
						try {
							// Set flag to prevent concurrent autosaves
							autosaveInProgress = true;

							// Prepare data for submission
							const payload = prepareInvoiceV2DataForSubmission(
								currentStateV2.formState,
								currentStateV2.invoicePositions,
							);

							// Dispatch the V2 autosave action
							store
								.dispatch(
									autosaveInvoiceV2({
										projectId:
											currentStateV2.formState.projectId,
										invoiceData: payload,
									}),
								)
								.then(() => {
									// Reset our tracking variables on success
									changesSinceLastAutosave = 0;
									lastAutosaveTime = Date.now();
									autosaveInProgress = false;
								})
								.catch((error) => {
									console.error(
										"❌ [AutosaveMiddlewareV2] Autosave failed:",
										error,
									);
									// Ensure flag is reset even on failure
									autosaveInProgress = false;
								});
						} catch (error) {
							console.error(
								"❌ [AutosaveMiddlewareV2] Error in autosave middleware:",
								error,
							);
							autosaveInProgress = false;
						}
					}
				}, AUTOSAVE_DEBOUNCE_MS);
			}
		}
	} else if (
		// Reset tracking when V2 form is explicitly saved or restored
		action.type === "invoiceV2/createInvoiceV2/fulfilled" ||
		action.type === "invoiceV2/updateInvoiceV2/fulfilled" ||
		action.type === "invoiceV2/autosaveInvoiceV2/fulfilled" ||
		action.type === "invoiceV2/resetForm" ||
		action.type === "invoiceV2/clearDirtyFlag"
	) {
		// Clear any existing timer
		if (autosaveTimer) {
			clearTimeout(autosaveTimer);
			autosaveTimer = null;
		}
		changesSinceLastAutosave = 0;
		lastAutosaveTime = Date.now();
		autosaveInProgress = false;
	}

	return result;
};

export default autosaveMiddlewareV2;
